import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Lock, ShieldCheck } from 'lucide-react';
import type { PlatformStaff } from '@worki/shared';
import { api } from '../../lib/api.js';
import { Spinner } from '../../components/ui.js';
import { AdminCard, Row } from './parts.js';

/**
 * ADM-005 — who at Worki may open any of this.
 *
 * This screen used to be a permissions matrix: what an `admin` inside a
 * business may do that a `member` may not. A Worki account is one person, so
 * every row of that matrix asked a question with one possible answer, and a
 * switch that nothing checks is a switch that lies.
 *
 * What is left is the question the matrix never answered and support actually
 * asks: who holds the keys right now. Two lists, and the difference between
 * them is the whole security story — the first needs a shell on the host to
 * change, which is what a stolen staff session cannot get and what a lockout is
 * recovered from; the second moves at the speed of somebody joining or leaving.
 *
 * Read-only, because the grant lives on the person. Copying the switch here
 * would be a second place to change one fact.
 */
export function StaffSection() {
  const { t } = useTranslation();

  const staff = useQuery({
    queryKey: ['platform', 'staff'],
    queryFn: () => api.get<PlatformStaff>('/platform/staff'),
  });

  if (staff.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  }

  const root = staff.data?.root ?? [];
  const granted = staff.data?.granted ?? [];

  return (
    <div className="space-y-4">
      <AdminCard title={t('admin.staff.root')} hint={t('admin.staff.rootHint')}>
        <ul className="space-y-1.5">
          {root.map((email) => (
            <li key={email} className="flex items-center gap-2 text-sm">
              <Lock className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
              <span dir="ltr" className="truncate">
                {email}
              </span>
            </li>
          ))}
          {root.length === 0 && <li className="text-sm text-muted-foreground">{t('admin.staff.rootEmpty')}</li>}
        </ul>
      </AdminCard>

      <AdminCard title={t('admin.staff.granted')} hint={t('admin.staff.grantedHint')}>
        {granted.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('admin.staff.grantedEmpty')}</p>
        ) : (
          <div className="space-y-2">
            {granted.map((person) => (
              <Row
                key={person.id}
                icon={<ShieldCheck className="h-4 w-4 text-primary" aria-hidden />}
                title={person.name}
                subtitle={
                  <span dir="ltr" className="inline-block max-w-full truncate">
                    {person.email} · {person.businessName}
                  </span>
                }
              />
            ))}
          </div>
        )}
        {/* The switch is on the person, one screen over. A link rather than a
            copy of it: two places to change one fact is how they disagree. */}
        <Link to="/platform/users" className="text-xs text-primary hover:underline">
          {t('admin.staff.manage')}
        </Link>
      </AdminCard>
    </div>
  );
}
